import type { ActionFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import { blockUsers } from '~/modules/user/api/block-users.server';

export const BLOCK_USER_API_ROUTE = '/remix-api/user/block';

const FORM_DATA_SCHEMA = z.object({
    memberID: z.coerce.number().int().positive(),
    unblock: z.boolean().default(false),
});

/**
 * API endpoint to report a user for moderation.
 * Use with the `useUserModeration` hook.
 * @see {@link packages/web/app/modules/moderation/hooks/use-user-moderation.ts}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const payload = await request.json();
    const result = FORM_DATA_SCHEMA.safeParse(payload);

    if (!result.success) {
        return routerData(null, { status: 400 });
    }

    try {
        const response = await blockUsers({
            context,
            blockMemberIDs: result.data.unblock ? [] : [result.data.memberID],
            unblockMemberIDs: result.data.unblock ? [result.data.memberID] : [],
        });

        if (!response) {
            return routerData(null, { status: 500 });
        }

        let statusCode = 200;
        if (
            response.__typename === 'NotAuthorizedError' ||
            response.__typename === 'NotFoundError'
        ) {
            statusCode = 403;
        } else if (response.__typename === 'UserError') {
            statusCode = 400;
        } else if (response.__typename === 'ServerError') {
            statusCode = 500;
        }

        return routerData({ response }, { status: statusCode });
    } catch (error) {
        logger.error(`Error in ${BLOCK_USER_API_ROUTE}`, { error });
        return routerData(null, { status: 500 });
    }
};
